<script lang="ts" setup>
import type { CollectionFieldPath, FilterRelationalQuantifier, GlobalFieldId, PrimitiveFieldPath } from '#layers/data-model/public';
import type { FilterPathSegment } from './utils/filter-path';
import { computed } from 'vue';
import { CollectionFieldPathSelect, FILTER_RELATIONAL_QUANTIFIERS, useDataModelDisplayNames } from '#layers/data-model/public';
import { useFilterGate } from '#layers/filters/composables/use-filter-gate';
import { isPrimitiveFieldPath, isRelationalSegment, isToManySegment } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { isEngineFilterableField } from './utils/is-engine-filterable-field';

const {
	includeRelationFields = true,
	enableRelationSelect = false,
	showQuantifiers = true,
	resolveJunctionTargets = false,
	segments,
	allowsSegmentPath,
	allowsQuantifier,
} = defineProps<{
	segments: FilterPathSegment[];
	includeRelationFields?: boolean;
	disabled?: boolean;
	/** Make relation fields selectable so clicking one emits `selectRelation` (re-targeting a related-item match). */
	enableRelationSelect?: boolean;
	/** Render the inline to-many quantifier selector. Off when the consumer owns its own quantifier control. */
	showQuantifiers?: boolean;
	/** Resolve M2M hops to their target collection. On when the segments hide junction hops (related-item path). */
	resolveJunctionTargets?: boolean;
	/** Whether the selector at a segment may select a path relative to it; defaults to the gate over the segments. */
	allowsSegmentPath?: (index: number, path: CollectionFieldPath) => boolean;
	/** Whether a to-many segment may switch to a quantifier; the consumer checks the clause that switch emits. */
	allowsQuantifier?: (index: number, quantifier: FilterRelationalQuantifier) => boolean;
}>();

const emit = defineEmits<{
	updateFromSegment: [index: number, path: PrimitiveFieldPath];
	updateQuantifier: [index: number, quantifier: FilterRelationalQuantifier];
	selectRelation: [index: number, relationFieldPath: string[]];
}>();

const gate = useFilterGate();

function segmentPath(index: number, path: readonly GlobalFieldId[] = []) {
	return [...segments.slice(0, index).map((segment) => segment.fieldId), ...path];
}

// A consumer whose segments hide junction hops passes its own check over the real path.
function allowsPath(index: number, path: CollectionFieldPath) {
	return allowsSegmentPath ? allowsSegmentPath(index, path) : gate.allowsPath(segmentPath(index, path));
}

function quantifierOptions(index: number) {
	return FILTER_RELATIONAL_QUANTIFIERS
		.filter((quantifier) => allowsQuantifier?.(index, quantifier) ?? true)
		.map((quantifier) => ({ label: quantifier.replace('_', ''), value: quantifier }));
}

const { findCollectionById, findFieldById, findOppositeCollectionByRelationFieldId, findM2MTargetCollectionByRelationFieldId } = injectFilterSchemaContext();
const { getFieldDisplayName } = useDataModelDisplayNames();
const includeSelectableRelationFields = computed(() => includeRelationFields !== false);
// Relations become terminal (selectable) only when the consumer wants a relation click to re-target.
const fieldPathTerminal = computed(() => enableRelationSelect ? 'primitive-or-relation' : 'primitive');

function getFieldName(segment: FilterPathSegment): string {
	const field = findFieldById(segment.fieldId);
	return field ? getFieldDisplayName(field) : segment.fieldId;
}

function getRootCollection() {
	const firstSegment = segments[0];
	const firstField = firstSegment ? findFieldById(firstSegment.fieldId) : undefined;

	return firstField?.collectionId ? findCollectionById(firstField.collectionId) : undefined;
}

function getSegmentSelectorCollection(index: number) {
	let collection = getRootCollection();

	if (!collection) return;

	for (const segment of segments.slice(0, index)) {
		if (!isRelationalSegment(segment)) return;

		// A related-item path hides M2M junction hops, so bridge an M2M hop to its target collection —
		// otherwise the next selector would list the junction's fields instead of the target's.
		const target = resolveJunctionTargets ? findM2MTargetCollectionByRelationFieldId?.(segment.fieldId) : undefined;
		const oppositeCollection = target ?? findOppositeCollectionByRelationFieldId(segment.fieldId);

		if (!oppositeCollection) return;

		collection = oppositeCollection;
	}

	return collection;
}

const segmentSelectorCollections = computed(() => segments.map((_, index) => getSegmentSelectorCollection(index)));

function onQuantifierChange(index: number, value: FilterRelationalQuantifier) {
	emit('updateQuantifier', index, value);
}

// A primitive selection updates the segment in place; selecting a relation itself re-targets the
// related-item match (only reachable when `enableRelationSelect` makes relations selectable).
function onSelect(index: number, path: CollectionFieldPath) {
	if (isPrimitiveFieldPath(path, { findFieldById })) {
		emit('updateFromSegment', index, path);
		return;
	}

	if (enableRelationSelect) emit('selectRelation', index, path);
}
</script>

<template>
	<div class="flex items-center">
		<template v-for="(segment, index) in segments" :key="index">
			<!-- Separator arrow -->
			<DIcon
				v-if="index > 0"
				name="lucide:chevron-right"
				class="size-3 shrink-0 text-dimmed"
			/>

			<CollectionFieldPathSelect
				v-if="segmentSelectorCollections[index]"
				:collection="segmentSelectorCollections[index]"
				include-constrained-fields
				:include-field="isEngineFilterableField"
				:include-relation-fields="includeSelectableRelationFields"
				:include-list-relation-fields="includeSelectableRelationFields"
				:terminal="fieldPathTerminal"
				size="md"
				:resolve-relation-collection="findOppositeCollectionByRelationFieldId"
				:filter-field-path="(path) => allowsPath(index, path)"
				:search-placeholder="$t('filters.filter_search_placeholder')"
				@select="onSelect(index, $event)"
			>
				<DButton
					color="neutral"
					variant="ghost"
					size="sm"
					class="rounded-none whitespace-nowrap"
					:ui="{ base: 'p-2' }"
					:disabled
				>
					{{ getFieldName(segment) }}
				</DButton>
			</CollectionFieldPathSelect>

			<template v-if="isToManySegment(segment) && showQuantifiers">
				<DIcon name="lucide:chevron-right" class="size-3 shrink-0 text-dimmed" />
				<DSelect
					:model-value="segment.quantifier"
					:items="quantifierOptions(index)"
					value-key="value"
					variant="ghost"
					trailing-icon=""
					class="rounded-none"
					:ui="{
						base: 'px-1',
						content: 'min-w-fit',
						value: 'text-xs text-primary',
					}"
					:disabled
					@update:model-value="(v: FilterRelationalQuantifier) => onQuantifierChange(index, v)"
				/>
			</template>
		</template>
	</div>
</template>
